'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import text from 'I18n/utils/unescapedText';
import UISelect from 'UIComponents/input/UISelect';
import Image from 'ContentComponents/cmv2/fields/Image';
import { getHasDismissedResponsiveImageWarning, getHasDismissedShowLoadingControls } from 'DesignData/selectors/app';
import { dismissResponsiveImageWarning, dismissShowLoadingWarning } from 'DesignData/actions/Application';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import { IMAGE } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { DEFAULT, RESIZABLE, RESPONSIVE, SHOW_LOADING } from 'DesignData/enums/CustomWidgetFieldAttributes';
import ImageSizeTypes from 'ContentUtils/constants/ImageSizeTypes';
import ToolFieldset from 'DesignComponents/tool/ToolFieldset';
import ToolFormControl from 'DesignComponents/tool/ToolFormControl';
import UIAlert from 'UIComponents/alert/UIAlert';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.contentOptions.${key}`;

const SIZE_CONTROL_TYPES = {
  ALL: 'ALL',
  EXACT_ONLY: 'EXACT_ONLY',
  NONE: 'NONE'
};
const LOADING_CONTROL_TYPES = {
  ALL: 'ALL',
  NONE: 'NONE'
};
const SIZE_CONTROL_TYPE_OPTIONS = Object.keys(SIZE_CONTROL_TYPES).map(_sizeControlType => {
  return {
    text: text(i18nKey(`image.sizeControlType.options.${_sizeControlType}`)),
    value: _sizeControlType
  };
});
const LOADING_CONTROL_OPTIONS = Object.keys(LOADING_CONTROL_TYPES).map(_sizeControlType => {
  return {
    text: text(i18nKey(`image.showLoadingType.options.${_sizeControlType}`)),
    value: _sizeControlType
  };
});

class ImageContentOptions extends Component {
  constructor(props) {
    super(props);

    this.onDefaultChange = value => {
      this.props.editField({
        [DEFAULT]: value
      });
    };

    this.onResizableChange = e => {
      this.props.editField({
        [RESIZABLE]: !e.target.checked
      });
    };

    this.onShowLoadingChange = e => {
      const sizeControlType = e.target.value;
      const changes = {
        [SHOW_LOADING]: sizeControlType === LOADING_CONTROL_TYPES.ALL
      };
      const showImageLoadingWarning = sizeControlType === LOADING_CONTROL_TYPES.ALL && this.loadingControlType === LOADING_CONTROL_TYPES.NONE;
      this.props.editField(changes);
      this.setState({
        showImageLoadingWarning
      });
    };

    this.onSizeControlTypeChange = e => {
      const sizeControlType = e.target.value;
      let changes = {};
      let showResponsiveImageWarning = false;

      switch (sizeControlType) {
        case SIZE_CONTROL_TYPES.ALL:
          {
            changes = {
              [RESIZABLE]: true,
              [RESPONSIVE]: true
            };

            if (this.sizeControlType === SIZE_CONTROL_TYPES.EXACT_ONLY) {
              showResponsiveImageWarning = true;
            }

            break;
          }

        case SIZE_CONTROL_TYPES.EXACT_ONLY:
          {
            changes = {
              [RESIZABLE]: true,
              [RESPONSIVE]: false,
              [DEFAULT]: Object.assign({}, this.props.field[DEFAULT], {
                size_type: ImageSizeTypes.EXACT
              })
            };
            break;
          }

        case SIZE_CONTROL_TYPES.NONE:
          {
            changes = {
              [RESIZABLE]: false
            };
            break;
          }

        default:
          break;
      }

      this.setState({
        showResponsiveImageWarning
      });
      this.props.editField(changes);
    };

    this.onResponsiveImageWarningClose = () => {
      this.setState({
        showResponsiveImageWarning: false
      });
      this.props.dismissResponsiveImageWarning();
    };

    this.onLoadingWarningClose = () => {
      this.setState({
        showImageLoadingWarning: false
      });
      this.props.dismissShowLoadingWarning();
    };

    this.state = {
      showResponsiveImageWarning: false,
      showImageLoadingWarning: false
    };
  }

  get sizeControlType() {
    if (!this.props.field[RESIZABLE]) return SIZE_CONTROL_TYPES.NONE;
    if (this.props.field[RESPONSIVE]) return SIZE_CONTROL_TYPES.ALL;
    return SIZE_CONTROL_TYPES.EXACT_ONLY;
  }

  get loadingControlType() {
    if (this.props.field[SHOW_LOADING]) return LOADING_CONTROL_TYPES.ALL;
    return LOADING_CONTROL_TYPES.NONE;
  }

  renderShowLoading() {
    const showImageLoadingWarning = !this.props.hasDismissedShowLoadingWarning && this.state.showImageLoadingWarning;
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(ToolFormControl, {
        className: "m-top-4",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('image.showLoadingType.label')
        }),
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('image.showLoadingType.tooltip')
        }),
        children: /*#__PURE__*/_jsx(UISelect, {
          "data-test-id": "module-image-field-size-dropdown",
          disabled: this.props.readOnly,
          options: LOADING_CONTROL_OPTIONS,
          onChange: this.onShowLoadingChange,
          required: true,
          value: this.loadingControlType
        })
      }), showImageLoadingWarning && /*#__PURE__*/_jsx(UIAlert, {
        className: "m-top-2",
        type: "warning",
        closeable: true,
        onClose: this.onLoadingWarningClose,
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey('image.showLoadingType.loadingControlWarning'),
          options: {
            loadingProperty: /*#__PURE__*/_jsx("code", {
              children: "loading"
            })
          }
        })
      })]
    });
  }

  renderResponsiveSizeControl() {
    const showResponsiveImageWarning = !this.props.hasDismissedResponsiveImageWarning && this.state.showResponsiveImageWarning;

    const sizeTypeProperty = /*#__PURE__*/_jsx("code", {
      children: "size_type"
    });

    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(ToolFormControl, {
        className: "m-top-4",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('image.sizeControlType.label')
        }),
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('image.sizeControlType.tooltip')
        }),
        children: /*#__PURE__*/_jsx(UISelect, {
          "data-test-id": "module-image-field-size-dropdown",
          disabled: this.props.readOnly,
          options: SIZE_CONTROL_TYPE_OPTIONS,
          onChange: this.onSizeControlTypeChange,
          required: true,
          value: this.sizeControlType
        })
      }), showResponsiveImageWarning && /*#__PURE__*/_jsx(UIAlert, {
        className: "m-top-2",
        type: "warning",
        closeable: true,
        onClose: this.onResponsiveImageWarningClose,
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey('image.sizeControlType.responsiveImageWarning'),
          options: {
            sizeTypeProperty
          }
        })
      })]
    });
  }

  render() {
    const {
      field,
      readOnly,
      isV1,
      isEmail
    } = this.props;
    return /*#__PURE__*/_jsxs(ToolFieldset, {
      children: [/*#__PURE__*/_jsx(ToolFormControl, {
        children: /*#__PURE__*/_jsx(Image, {
          label: text(i18nKey(`defaultLabels.${IMAGE}`)),
          onFieldChange: this.onDefaultChange,
          value: field[DEFAULT],
          readOnly: readOnly,
          resizable: field[RESIZABLE],
          responsive: field[RESPONSIVE],
          forceHideResponsive: isEmail,
          show_loading: field[SHOW_LOADING],
          forceShowLoading: !isEmail
        })
      }), !isV1 && this.renderResponsiveSizeControl(), !isV1 && this.renderShowLoading()]
    });
  }

}

ImageContentOptions.propTypes = {
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  readOnly: PropTypes.bool,
  isV1: PropTypes.bool.isRequired,
  isEmail: PropTypes.bool.isRequired,
  hasDismissedResponsiveImageWarning: PropTypes.bool.isRequired,
  dismissResponsiveImageWarning: PropTypes.func.isRequired,
  dismissShowLoadingWarning: PropTypes.func.isRequired,
  hasDismissedShowLoadingWarning: PropTypes.bool.isRequired
};

function mapStateToProps(state) {
  return {
    hasDismissedResponsiveImageWarning: getHasDismissedResponsiveImageWarning(state),
    hasDismissedShowLoadingWarning: getHasDismissedShowLoadingControls(state)
  };
}

const mapDispatchToProps = {
  dismissResponsiveImageWarning,
  dismissShowLoadingWarning
};
export default connect(mapStateToProps, mapDispatchToProps)(ImageContentOptions);